<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

import { MOVE_LABELS, listStockMoves, type MoveRow } from '../api/stock'
import { listWarehouses, type Warehouse } from '../api/warehouses'

const router = useRouter()

const rows = ref<MoveRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)

const warehouses = ref<Warehouse[]>([])
const refError = ref<unknown>(null)

// 参照下拉用 undefined 表示「未选」：0 会被 el-select 当成值显示成「0」，placeholder 就没了
const query = reactive({
  keyword: '',
  warehouse: undefined as number | undefined,
  from: '',
  to: '',
  page: 1,
  size: 20,
})

async function load(): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listStockMoves({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      warehouse: query.warehouse || undefined,
      from: query.from || undefined,
      to: query.to || undefined,
    })
    rows.value = res.list
    total.value = res.total
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
  listWarehouses({ page: 1, size: 100 })
    .then((res) => {
      warehouses.value = res.list
    })
    .catch((e) => {
      // 仓库列表拿不到筛选下拉会空，但必须把原因露出来（如未选货主时后端回 422）
      refError.value = e
    })
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function back(): void {
  void router.push({ name: 'stocks' })
}

/** 流水方向：入库为正、出库为负，颜色一眼看出是进还是出 */
function deltaType(qty: number): 'success' | 'danger' | 'info' {
  if (qty > 0) return 'success'
  if (qty < 0) return 'danger'
  return 'info'
}
</script>

<template>
  <div class="yc-mv">
    <header class="yc-mv__bar">
      <el-button @click="back">返回库存</el-button>
      <el-input
        v-model="query.keyword"
        class="yc-mv__keyword"
        placeholder="商品编码/名称/条码或单据号"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.warehouse" class="yc-mv__ref" placeholder="全部仓库" clearable @change="search">
        <el-option
          v-for="w in warehouses"
          :key="w.id"
          :label="`${w.code} ${w.name}`.trim()"
          :value="w.id"
        />
      </el-select>
      <el-date-picker v-model="query.from" type="date" value-format="YYYY-MM-DD" placeholder="开始日期" clearable />
      <el-date-picker v-model="query.to" type="date" value-format="YYYY-MM-DD" placeholder="结束日期" clearable />
      <el-button @click="search">查询</el-button>
      <el-button @click="load()">刷新</el-button>
    </header>

    <div v-if="error || refError" class="yc-mv__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-mv__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column label="时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.moved_at) }}</template>
        </el-table-column>
        <el-table-column label="类型" width="110">
          <template #default="{ row }">
            <el-tag size="small">{{ MOVE_LABELS[row.move_type] ?? row.move_type }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="sku_code" label="商品编码" width="130" />
        <el-table-column prop="sku_name" label="商品名称" min-width="150" show-overflow-tooltip />
        <el-table-column label="批次" width="120">
          <template #default="{ row }">{{ row.batch_no || '—' }}</template>
        </el-table-column>
        <el-table-column label="数量" width="90">
          <template #default="{ row }">
            <el-text :type="deltaType(row.qty_delta)">
              {{ row.qty_delta > 0 ? `+${row.qty_delta}` : row.qty_delta }}
            </el-text>
          </template>
        </el-table-column>
        <el-table-column label="从 → 到" min-width="180">
          <template #default="{ row }">
            {{ row.from_location_code || '—' }} → {{ row.to_location_code || '—' }}
          </template>
        </el-table-column>
        <el-table-column label="关联单据" min-width="150">
          <template #default="{ row }">{{ row.ref_no || '—' }}</template>
        </el-table-column>
        <el-table-column prop="operator" label="操作人" width="110" />
      </el-table>

      <EmptyState v-else-if="!loading" title="还没有流水" description="收货、上架、移库、盘点都会在这里留下记录。" />
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-mv__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />
  </div>
</template>

<style scoped lang="scss">
.yc-mv {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 240px;
  }

  &__ref {
    width: 180px;
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }
}
</style>
